import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { filled?: boolean };

function Svg({ filled, children, ...props }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" {...props}>
      {children}
    </svg>
  );
}

export function IconHome({ filled, ...props }: IconProps) {
  return (
    <Svg filled={filled} {...props}>
      {filled ? (
        <path fill="currentColor" d="M11.3 2.2a1.2 1.2 0 0 1 1.4 0l8.2 6.4a1 1 0 0 1 .4.8V20a1.8 1.8 0 0 1-1.8 1.8h-5.2v-7.1h-4.8V21.8H4.5A1.8 1.8 0 0 1 2.7 20v-10.6a1 1 0 0 1 .4-.8Z" />
      ) : (
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
          d="M4.2 10.2 12 3.8l7.8 6.4V20a1.2 1.2 0 0 1-1.2 1.2h-4.4v-6.6H9.8V21.2H5.4A1.2 1.2 0 0 1 4.2 20Z"
        />
      )}
    </Svg>
  );
}

export function IconCamera({ filled, ...props }: IconProps) {
  return (
    <Svg filled={filled} {...props}>
      {filled ? (
        <path fill="currentColor" d="M9.2 4.2h5.6l1.2 1.8H19A2.2 2.2 0 0 1 21.2 8.2v9.6A2.2 2.2 0 0 1 19 20H5a2.2 2.2 0 0 1-2.2-2.2V8.2A2.2 2.2 0 0 1 5 6h3Zm2.8 4.6A4.2 4.2 0 1 0 16.2 13 4.2 4.2 0 0 0 12 8.8Z" />
      ) : (
        <>
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
            d="M8.8 5.2h6.4l1.1 1.8H19a1.8 1.8 0 0 1 1.8 1.8v8.4A1.8 1.8 0 0 1 19 19H5a1.8 1.8 0 0 1-1.8-1.8V8.8A1.8 1.8 0 0 1 5 7h2.7Z"
          />
          <circle cx="12" cy="13" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </>
      )}
    </Svg>
  );
}

export function IconSdk({ filled, ...props }: IconProps) {
  return (
    <Svg filled={filled} {...props}>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth={filled ? "2.2" : "1.8"}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.2 8.2 4.4 12l3.8 3.8M15.8 8.2 19.6 12l-3.8 3.8M13.2 6.5l-2.4 11"
      />
    </Svg>
  );
}

export function IconUsers({ filled, ...props }: IconProps) {
  return (
    <Svg filled={filled} {...props}>
      {filled ? (
        <>
          <circle cx="9" cy="8" r="3.2" fill="currentColor" />
          <circle cx="16.2" cy="9.2" r="2.6" fill="currentColor" />
          <path fill="currentColor" d="M3.2 19.2c.5-3.2 2.9-5 5.8-5s5.3 1.8 5.8 5H3.2Z" />
          <path fill="currentColor" d="M12.8 19.2c.3-1.8 1.2-3.2 2.8-4 .9-.4 1.9-.6 2.9-.6 2.1 0 3.8 1.1 4.3 3.4v1.2h-10Z" />
        </>
      ) : (
        <>
          <circle cx="9" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="16.2" cy="9.2" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            d="M3.4 19c.5-3 2.7-4.6 5.6-4.6S14.1 16 14.6 19"
          />
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            d="M14.2 14.8c1-.6 2.1-.9 3.4-.9 2 0 3.6 1 4.2 3.1"
          />
        </>
      )}
    </Svg>
  );
}

export function IconSettings({ filled, ...props }: IconProps) {
  return (
    <Svg filled={filled} {...props}>
      <path
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        d="M10.1 3.4h3.8l.6 2.2 2.1.9 2-1.2 2.7 2.7-1.2 2 .9 2.1 2.2.6v3.8l-2.2.6-.9 2.1 1.2 2-2.7 2.7-2-1.2-2.1.9-.6 2.2h-3.8l-.6-2.2-2.1-.9-2 1.2-2.7-2.7 1.2-2-.9-2.1-2.2-.6v-3.8l2.2-.6.9-2.1-1.2-2 2.7-2.7 2 1.2 2.1-.9Z"
      />
      <circle cx="12" cy="12" r="2.6" fill={filled ? "var(--bg, #0b1020)" : "none"} stroke="currentColor" strokeWidth="1.8" />
    </Svg>
  );
}

export function IconProfile({ filled, ...props }: IconProps) {
  return (
    <Svg filled={filled} {...props}>
      <circle cx="12" cy="8.2" r="3.4" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" />
      <path
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        d="M5 19.2c.6-3.2 3.2-5 7-5s6.4 1.8 7 5"
      />
    </Svg>
  );
}

export function IconEye({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" d="M2.8 12s3.4-6.2 9.2-6.2S21.2 12 21.2 12s-3.4 6.2-9.2 6.2S2.8 12 2.8 12Z" />
      <circle cx="12" cy="12" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </Svg>
  );
}

export function IconEyeOff({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M4 4.8 20 19.2" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" d="M9.2 8.4A6.8 6.8 0 0 1 12 7.8c5.8 0 9.2 6.2 9.2 6.2a16 16 0 0 1-3.1 3.4M6.6 6.9C4.2 8.5 2.8 12 2.8 12s3.4 6.2 9.2 6.2c1.2 0 2.3-.2 3.3-.6" />
    </Svg>
  );
}

export function IconChevron({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="m9 5.5 7 6.5-7 6.5" />
    </Svg>
  );
}

export function IconBack({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M15 5.5 8 12l7 6.5" />
    </Svg>
  );
}

export function IconKey({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="8" cy="14" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M11 12.2 19.4 5.8m-3.6.4 2.8 2.8" />
    </Svg>
  );
}

export function IconDocs({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" d="M7 4.5h7.2L19 9.2V19a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V6A1.5 1.5 0 0 1 7 4.5Z" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" d="M14 4.8v4.8h5" />
    </Svg>
  );
}

export function IconLogs({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M5 7h14M5 12h14M5 17h9" />
    </Svg>
  );
}

export function IconMail({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="6.2" width="17" height="11.6" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="m4.2 7.4 7.8 5.4 7.8-5.4" />
    </Svg>
  );
}

export function IconLock({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <rect x="5.5" y="10.5" width="13" height="9.2" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" d="M8.2 10.5V8.2a3.8 3.8 0 0 1 7.6 0v2.3" />
    </Svg>
  );
}

export function IconFingerprint({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M12 4.8c2.6 0 4.8 2 5.3 4.6" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M6.6 9.2A5.4 5.4 0 0 1 12 4.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M8.2 19.4c.6-2.2 1.2-6.8 1.2-8.2A2.6 2.6 0 0 1 12 8.6c1.4 0 2.6 1.1 2.6 2.6 0 .8-.2 3.4-.6 5.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M15.8 18.6c.8-2.8 1.3-6 1.3-7.4A5.1 5.1 0 0 0 12 6.2 5.1 5.1 0 0 0 6.9 11.2c0 1.6.4 5.1 1 7.4" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M12 11.2c0 1.8-.4 5.2-.8 7.4" />
    </Svg>
  );
}

export function IconFace({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M9 10.2h.01M15 10.2h.01" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M8.8 14.2c.8 1.4 2 2.2 3.2 2.2s2.4-.8 3.2-2.2" />
    </Svg>
  );
}

export function IconCode({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="m8 8-4 4 4 4M16 8l4 4-4 4M13.2 6.5l-2.4 11" />
    </Svg>
  );
}

export function IconOverview({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4" y="4.5" width="7" height="7" rx="1.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <rect x="13" y="4.5" width="7" height="7" rx="1.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <rect x="4" y="13.5" width="7" height="7" rx="1.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <rect x="13" y="13.5" width="7" height="7" rx="1.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </Svg>
  );
}

export function IconLogout({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M10 12h9m-3-3.5 3.5 3.5L16 15.5M14 5.5H7.5A1.5 1.5 0 0 0 6 7v10a1.5 1.5 0 0 0 1.5 1.5H14" />
    </Svg>
  );
}

export function IconPlus({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M12 5v14M5 12h14" />
    </Svg>
  );
}

export function IconEdit({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5 9 18l9.4-9.4a1.8 1.8 0 0 0 0-2.6L16 3.6a1.8 1.8 0 0 0-2.6 0L4 13.1Zm9.2-13.6 2.4 2.4" />
    </Svg>
  );
}

export function IconTrash({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M5 7h14M9.2 7V5.4A1.4 1.4 0 0 1 10.6 4h2.8A1.4 1.4 0 0 1 14.8 5.4V7M8.2 7l.6 12.2A1.4 1.4 0 0 0 10.2 20.6h3.6a1.4 1.4 0 0 0 1.4-1.4L15.8 7" />
    </Svg>
  );
}

export function IconCopy({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" d="M15.5 8.4V6.5A1.5 1.5 0 0 0 14 5H6.5A1.5 1.5 0 0 0 5 6.5V14a1.5 1.5 0 0 0 1.5 1.5h1.9" />
    </Svg>
  );
}

export function IconRefresh({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M19.2 12a7.2 7.2 0 1 1-2.1-5.1L19.2 8.2M19.2 4.8v3.4h-3.4" />
    </Svg>
  );
}

export function IconPause({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M9 6.5v11M15 6.5v11" />
    </Svg>
  );
}

export function IconPlay({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" d="M8.5 6.2 18 12 8.5 17.8Z" />
    </Svg>
  );
}

export function IconBan({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M7 17 17 7" />
    </Svg>
  );
}

export function IconDownload({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M12 5v10.5M8 12.2l4 4 4-4M5.5 19h13" />
    </Svg>
  );
}

export function IconShare({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.2 6.4 20 12l-4.8 5.6M20 12H10.2M8.2 6.8H6.4A1.6 1.6 0 0 0 4.8 8.4v7.2a1.6 1.6 0 0 0 1.6 1.6h1.8"
      />
    </Svg>
  );
}

export function IconClose({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="m6.5 6.5 11 11M17.5 6.5l-11 11" />
    </Svg>
  );
}

export function IconCameraAdd({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M8.5 6h3.2l1 1.6H18a1.6 1.6 0 0 1 1.6 1.6V17a1.6 1.6 0 0 1-1.6 1.6H6A1.6 1.6 0 0 1 4.4 17V9.2A1.6 1.6 0 0 1 6 7.6h1.4Z" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M12 10.8v5.2M9.4 13.4h5.2" />
    </Svg>
  );
}

export function IconCheck({ ...props }: IconProps) {
  return (
    <Svg {...props}>
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="m5.5 12.2 4.2 4.3 8.8-9" />
    </Svg>
  );
}
